import { useSyncExternalStore, type MouseEvent } from 'react'
import { DateTime } from 'luxon'
import { X } from 'lucide-react'

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '~/client/components/ui/drawer'
import { Separator } from '~/client/components/ui/separator'
import { Skeleton } from '~/client/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/client/components/ui/tooltip'
import { useVenueTrustSignalDetails } from '~/client/venue/venue-trust-signal.hooks'
import eaterLogo from '~/assets/images/eater-logo.svg'
import infatuationFavicon from '~/assets/images/infatuation-favicon.png'
import {
  EDITORIAL_RECOGNITION_TRUST_SIGNALS,
  VENUE_TRUST_SIGNAL_LABELS,
  VenueTrustSignalDomain,
} from '~/shared/venue/trust-signals'

const EDITORIAL_RECOGNITION_BADGE_META: Record<VenueTrustSignalDomain, { label: string; src: string }> = {
  [VenueTrustSignalDomain.Eater]: {
    label: VENUE_TRUST_SIGNAL_LABELS[VenueTrustSignalDomain.Eater],
    src: eaterLogo,
  },
  [VenueTrustSignalDomain.Infatuation]: {
    label: VENUE_TRUST_SIGNAL_LABELS[VenueTrustSignalDomain.Infatuation],
    src: infatuationFavicon,
  },
}

type EditorialRecognitionDrawerSelection = {
  venueId: number
  signal: VenueTrustSignalDomain
} | null

let editorialRecognitionDrawerSelection: EditorialRecognitionDrawerSelection = null
const editorialRecognitionDrawerListeners = new Set<() => void>()

function subscribeToEditorialRecognitionDrawer(listener: () => void): () => void {
  editorialRecognitionDrawerListeners.add(listener)
  return () => editorialRecognitionDrawerListeners.delete(listener)
}

function getEditorialRecognitionDrawerSnapshot(): EditorialRecognitionDrawerSelection {
  return editorialRecognitionDrawerSelection
}

function setEditorialRecognitionDrawerSelection(nextSelection: EditorialRecognitionDrawerSelection) {
  editorialRecognitionDrawerSelection = nextSelection
  editorialRecognitionDrawerListeners.forEach((listener) => listener())
}

export const getEditorialRecognitionTrustSignals = (trustSignals: readonly string[]): VenueTrustSignalDomain[] =>
  EDITORIAL_RECOGNITION_TRUST_SIGNALS.filter((signal) => trustSignals.includes(signal))

function formatSignalDate(value: string | null): string | null {
  if (!value) return null

  const parsed = DateTime.fromISO(value, { setZone: true })
  return parsed.isValid ? parsed.toFormat('LLL d, yyyy') : value
}

type EditorialRecognitionBadgesProps = {
  trustSignals: readonly string[]
  venueId?: number | null
  size?: 'default' | 'metric' | 'tooltip'
  shrinkEaterLogo?: boolean
  enlargeInfatuationLogo?: boolean
  interactive?: boolean
}

export function EditorialRecognitionBadges({
  trustSignals,
  venueId,
  size = 'default',
  shrinkEaterLogo = false,
  enlargeInfatuationLogo = false,
  interactive = true,
}: EditorialRecognitionBadgesProps) {
  const recognizedSignals = getEditorialRecognitionTrustSignals(trustSignals)

  if (recognizedSignals.length === 0) return null

  const handleBadgeClick = (signal: VenueTrustSignalDomain) => (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (!venueId) return
    setEditorialRecognitionDrawerSelection({ venueId, signal })
  }

  const isMetricSize = size === 'metric'
  const isTooltipSize = size === 'tooltip'
  const gapClass = isMetricSize ? 'gap-[6px]' : isTooltipSize ? 'gap-[5px]' : 'gap-[8px]'
  const buttonSizeClass = isMetricSize ? 'size-[18px]' : isTooltipSize ? 'size-[24px]' : 'size-[30px]'
  const imageSizeClass = isMetricSize ? 'size-[18px]' : 'size-[24px]'
  const compactEaterImageSizeClass = isMetricSize ? 'size-[16px]' : isTooltipSize ? 'h-[18px] w-auto' : 'size-[22px]'
  const enlargedInfatuationImageSizeClass = isMetricSize ? 'size-[26px]' : isTooltipSize ? 'size-[24px]' : 'size-[26px]'
  const getSignalImageSizeClass = (signal: VenueTrustSignalDomain) => {
    if (shrinkEaterLogo && signal === VenueTrustSignalDomain.Eater) return compactEaterImageSizeClass
    if (enlargeInfatuationLogo && signal === VenueTrustSignalDomain.Infatuation) return enlargedInfatuationImageSizeClass
    return imageSizeClass
  }

  if (!interactive) {
    return (
      <div
        className={`flex items-center ${gapClass}`}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
        }}
      >
        {recognizedSignals.map((signal) => {
          const meta = EDITORIAL_RECOGNITION_BADGE_META[signal]
          const signalImageSizeClass = getSignalImageSizeClass(signal)

          return (
            <span
              key={signal}
              className={[
                'inline-flex items-center justify-center rounded-[4px] bg-transparent',
                buttonSizeClass,
              ].join(' ')}
            >
              <img src={meta.src} alt="" className={`${signalImageSizeClass} rounded-[3px]`} />
              <span className="sr-only">{meta.label}</span>
            </span>
          )
        })}
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={0}>
      <div className={`flex items-center ${gapClass}`}>
        {recognizedSignals.map((signal) => {
          const meta = EDITORIAL_RECOGNITION_BADGE_META[signal]
          const signalImageSizeClass = getSignalImageSizeClass(signal)

          return (
            <Tooltip key={signal}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`View ${meta.label} editorial recognition`}
                  className={[
                    'inline-flex items-center justify-center rounded-[4px] bg-transparent transition-opacity hover:opacity-80 disabled:cursor-default disabled:opacity-60',
                    buttonSizeClass,
                  ].join(' ')}
                  disabled={!venueId}
                  onClick={handleBadgeClick(signal)}
                >
                  <img src={meta.src} alt="" className={`${signalImageSizeClass} rounded-[3px]`} />
                  <span className="sr-only">{meta.label}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">{meta.label}: View articles mentioning this venue</TooltipContent>
            </Tooltip>
          )
        })}
      </div>
    </TooltipProvider>
  )
}

export function EditorialRecognitionDrawerHost() {
  const drawerSelection = useSyncExternalStore(
    subscribeToEditorialRecognitionDrawer,
    getEditorialRecognitionDrawerSnapshot,
    getEditorialRecognitionDrawerSnapshot,
  )
  const selectedMeta = drawerSelection ? EDITORIAL_RECOGNITION_BADGE_META[drawerSelection.signal] : null

  const closeDrawer = () => {
    setEditorialRecognitionDrawerSelection(null)
  }

  return (
    <Drawer
      dismissible
      direction="right"
      open={drawerSelection !== null}
      onOpenChange={(open) => {
        if (!open) closeDrawer()
      }}
    >
      {drawerSelection ? (
        <DrawerContent
          className="left-auto right-0 top-0 bottom-auto mt-0 h-full w-[440px] max-w-[calc(100vw-24px)] rounded-none border-l border-[#e4e1e9] bg-white p-0 shadow-[-16px_0_40px_rgba(24,0,61,0.12)] [&>div:first-child]:hidden"
          overlayClassName="bg-transparent"
          overlayProps={{
            onPointerDown: (event) => {
              event.stopPropagation()
              closeDrawer()
            },
          }}
          onClick={(event) => {
            event.stopPropagation()
          }}
          onEscapeKeyDown={closeDrawer}
          onPointerDown={(event) => {
            event.stopPropagation()
          }}
        >
          <DrawerHeader className="border-b border-[#eeeaf4] px-[24px] py-[20px] text-left">
            <div className="flex items-start justify-between gap-[16px]">
              <div className="flex min-w-0 items-center gap-[12px]">
                {selectedMeta ? <img src={selectedMeta.src} alt="" className="size-[28px] rounded-[4px]" /> : null}
                <div className="min-w-0">
                  <DrawerTitle className="font-['Inter:Semi_Bold',_sans-serif] text-[18px] text-[#18003d]">
                    {selectedMeta?.label ?? 'Editorial Recognition'}
                  </DrawerTitle>
                  <DrawerDescription className="mt-[4px] text-[13px] text-[#7c7483]">
                    Editorial mentions, ordered newest first.
                  </DrawerDescription>
                </div>
              </div>
              <button
                type="button"
                className="inline-flex size-[32px] shrink-0 items-center justify-center rounded-[6px] text-[#7c7483] transition-colors hover:bg-[#f5f2fa] hover:text-[#18003d]"
                aria-label="Close editorial recognition details"
                onClick={closeDrawer}
              >
                <X className="size-[16px]" />
              </button>
            </div>
          </DrawerHeader>

          <EditorialRecognitionDrawerBody selection={drawerSelection} />
        </DrawerContent>
      ) : null}
    </Drawer>
  )
}

function EditorialRecognitionDrawerBody({ selection }: { selection: Exclude<EditorialRecognitionDrawerSelection, null> }) {
  const queryResult = useVenueTrustSignalDetails(selection.venueId, selection.signal)
  const { data: signalDetails = [], isLoading, isError } = queryResult

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-[24px] py-[20px]">
      {isLoading ? (
        <div className="flex flex-col gap-[18px]">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="flex flex-col gap-[10px]">
              <Skeleton className="h-[18px] w-4/5" />
              <Skeleton className="h-[14px] w-2/5" />
              <Skeleton className="h-[54px] w-full" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-[8px] border border-[#f0d3d0] bg-[#fff8f7] px-[14px] py-[12px] text-[14px] text-[#8f302b]">
          We couldn&apos;t load these editorial details. Please try again.
        </div>
      ) : signalDetails.length === 0 ? (
        <div className="rounded-[8px] border border-[#eeeaf4] bg-[#fbfafc] px-[14px] py-[12px] text-[14px] text-[#6a6262]">
          Editorial details are currently under review.
        </div>
      ) : (
        <div className="flex flex-col">
          {signalDetails.map((detail, index) => {
            const publishedDate = formatSignalDate(detail.sourcePublishedDate)
            const updatedDate = formatSignalDate(detail.sourceLastUpdatedDate)

            return (
              <div key={`${detail.sourceTitle}-${detail.signalUrl ?? index}`} className="flex flex-col gap-[10px] py-[16px] first:pt-0">
                <div className="flex flex-col gap-[6px]">
                  <div className="font-['Inter:Semi_Bold',_sans-serif] text-[15px] leading-[1.35] text-[#18003d]">
                    {detail.sourceTitle}
                  </div>

                  {(publishedDate || updatedDate) && (
                    <div className="flex flex-wrap gap-x-[12px] gap-y-[4px] text-[12px] text-[#8c8591]">
                      {publishedDate ? <span>Published {publishedDate}</span> : null}
                      {updatedDate ? <span>Updated {updatedDate}</span> : null}
                    </div>
                  )}
                </div>

                {detail.venueDescription ? (
                  <p className="text-[14px] leading-[1.55] text-[#4b4453]">
                    {detail.venueDescription}
                  </p>
                ) : null}

                {detail.signalUrl ? (
                  <div className="min-w-0 text-[12px] leading-[1.45] text-[#8c8591]">
                    <span>Source: </span>
                    <a
                      href={detail.signalUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="break-all text-[#5f35d5] underline decoration-[#c9bdf4] underline-offset-[2px] hover:text-[#4021a8]"
                    >
                      {detail.signalUrl}
                    </a>
                  </div>
                ) : null}

                {index < signalDetails.length - 1 ? <Separator className="mt-[6px] bg-[#eeeaf4]" /> : null}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
